import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  data: [],
  newNotification: false,
};

// gauntlet-realtime3 N7.18 (2026-08-06): UNBOUNDED store growth, MEASURED
// (VALIDATOR w3-notif-1 PASS8, 2026-07-27) — a 267-row/27-page feed paginated
// strictly monotonically with no plateau/eviction; every loaded row is retained
// in this store + the DOM forever. At the extreme (500+ pushes in a session)
// that is unbounded memory/perf growth. Cap total stored rows so pagination and
// any future live-push channel can never grow the store past a fixed ceiling.
// 500 is generous headroom over any normal session (the measured 267-row feed
// fits with room to spare) while bounding the worst case. Newest rows are kept
// (state.data is loaded newest-first; overflow is trimmed off the END).
const MAX_STORED_NOTIFICATIONS = 500;

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications: (state, action) => {
      state.data = action.payload.slice(0, MAX_STORED_NOTIFICATIONS);
    },
    clearNewNotification: (state) => {
      state.newNotification = false;
    },
    setNewNotificationTrue: (state) => {
      state.newNotification = true;
    },
    // 6.10: the same push can legitimately arrive twice (foreground + background
    // FCM listeners both firing for one messageId, or a second device relaying
    // the same server-side event) — drop the repeat instead of growing a
    // duplicate row for it.
    addNotification: (state, action) => {
      if (state.data.some((n) => n && n.id === action.payload.id)) {
        return;
      }
      state.data = [action.payload, ...state.data].slice(0, MAX_STORED_NOTIFICATIONS);
    },
    addNotifications: (state, action) => {
      state.data = [...state.data, ...action.payload].slice(0, MAX_STORED_NOTIFICATIONS);
    },
    // P5S1-O12: flip a single notification to read (by id) so its unread dot
    // clears immediately when the user activates the row.
    markNotificationRead: (state, action) => {
      const id = action.payload;
      state.data = state.data.map((n) =>
        n && n.id === id ? { ...n, is_read: true } : n
      );
    },
    // kelly-0902-notif-single-kebab-with-full-option-set: the full /notifications
    // page's kebab Delete action needs to drop the deleted row from THIS store's
    // list (the page renders off `state.notifications.data`, not react-query) so
    // the row disappears immediately, matching the bell popover's optimistic
    // removal.
    removeNotification: (state, action) => {
      const id = action.payload;
      state.data = state.data.filter((n) => !(n && n.id === id));
    },
  },
});

export const {
  setNotifications,
  clearNewNotification,
  setNewNotificationTrue,
  addNotification,
  addNotifications,
  markNotificationRead,
  removeNotification,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
